<template>
  <div :style="colors">
    <a ref="skipLink" class="skip-link screen-reader-text" href="#main">Skip to content</a>
    <Alert ref="cg-alert" :type="alertVariant" :title="alertTitle" />
    <Loader :show="showLoader"/>

    <div class="user-sidebar-wrapper hidden lg:block">
      <div v-if="loaded" class="user-sidebar">
        <div class="user-sidebar-actions">

          <div v-if="showChatBot" :title="$t('ChatBot.CyberAdvisor')" @click="$router.push('/cyber-advisor')">
            <button class="cyber-button" aria-label="$t('ChatBot.CyberAdvisor')">
              <img :src="cyberIcon" :alt="$t('ChatBot.CyberAdvisor')" :width="24" :height="24" />
            </button>
          </div>

          <!-- User notifications -->
          <Notifications :notifications="notifications" />

          <!-- User profile menu -->
          <ProfileMenu menu-placement="bottom-end" :display-name="userInitials" show-indicator>
            <div class="m-2 text-center">
              <span v-if="$impersonating && !$user.isDemoAccount" class="ml-1 font-thin block text-xs">{{impersonator}}</span>
              <span class="ml-1 font-bold block">{{$user.isDemoAccount? impersonator : userName}}</span>
            </div>
            <hr />

            <!-- TODO: user roles should contain all roles (for example both 'manager' and 'student' roles) -->
            <div v-if="$company.roles.length>1">
              <span class="ml-2 mt-2 font-bold">{{$t('Navbar.Roles')}}</span>
              <MenuItem
                v-for="(r, i) in $company.roles" :key="i"
                icon-name="restart"
                :weight="($profile.currentRole === r.name)? 'bold':''"
                @click="($user.isDemoAccount)? $stopImpersonation(r) : setCurrentRole(r)"
              >
                {{ $t(`Roles.RoleNames.${$methods.getBaseRole(r.name)}`) }}
              </MenuItem>
              <hr role="separator" />
            </div>
            <MenuItem v-if="!$impersonating" to="/profile" icon-name="settings">{{ $t('Navbar.Profile') }}</MenuItem>
            <MenuItem v-if="$impersonating && !$user.isDemoAccount" icon-name="user" color="accent" @click="$stopImpersonation">
              {{ $t('Account.StopImpersonate') }}
            </MenuItem>
            <hr role="separator" />
            <MenuItem icon-name="logout" @click="logOut">{{ $t('Navbar.LogOut') }}</MenuItem>
          </ProfileMenu>
        </div>
      </div>
    </div>

    <PageWrapper id="main" role="main" :aria-label="$t('Wcag.MainContent')" class="bg-extra-light relative">
      <template v-if='loaded' #navbar>
        <PolicyModal :show-modal="showWelcomeModal" :trigger="$refs.allModulesRef"/>
        <PrivacyNoticeModal />
        <Navbar ref='navbarRef' :footnote="$t('General.Cyberguru')" :logoLinkTo="$getRoleBaseRoute()">
          <template #logo>
            <Logo />
          </template>
          <template #logo-small>
            <LogoSymbol />
          </template>
          <template #main-menu>
            <NavbarItem v-for="(item, i) in visibleNavbarItems" :key="i" :aria-label="item.getLabel() " :active="isActiveItem(item)" :to='item.getPath() ' :icon='item.icon'>
              {{ item.getLabel() }}
            </NavbarItem>
          </template>
          <template #footer-menu>
            <NavbarItem v-if='$company.roles.length>1' :aria-label="$t('Navbar.Roles')" class='block lg:hidden' to='#'>
              <span>{{ $t('Navbar.Roles') }}</span>
              <MenuItem
                v-for='(r, i) in $company.roles' :key='i' :weight="($profile.currentRole === r.name)? 'bold':''"
                icon-name='restart' @click='setCurrentRole(r)'>
                {{ $t(`Roles.RoleNames.${$methods.getBaseRole(r.name)}`) }}
              </MenuItem>
            </NavbarItem>
            <NavbarItem v-for="(item, i) in footerNavbarItems" :key="i" :aria-label="item.getLabel()" :class="item.hasClass" :active="isActiveItem(item)" :to='item.getPath() ' :icon='item.icon'>
              {{ item.getLabel() }}
            </NavbarItem>
            <NavbarItem v-if="$impersonating && !$user.isDemoAccount" :aria-label="$t('Account.StopImpersonate')" class='block lg:hidden' icon="user" @click="$stopImpersonation">
                {{ $t('Account.StopImpersonate') }}
            </NavbarItem>
            <NavbarItem :aria-label="$t('Navbar.LogOut')" icon='logout' @click='logOut'>{{ $t('Navbar.LogOut') }}</NavbarItem>
          </template>
          <!-- CG20-6134: pagina statica fuori dalla SPA, apertura in nuova tab per non perdere la sessione applicativa -->
          <template #footnote-links>
            <a class="underline hover:no-underline" :href="`/privacy-cookie-policy.html?lang=${policyLang}`" target="_blank" rel="noopener noreferrer">
              {{ $t('Navbar.PrivacyPolicy') }}
            </a>
            <a class="underline hover:no-underline" :href="`/privacy-cookie-policy.html?lang=${policyLang}#cookie`" target="_blank" rel="noopener noreferrer">
              {{ $t('Navbar.CookiePolicy') }}
            </a>
          </template>
          <template #footnote-links-collapsed>
            <a :href="`/privacy-cookie-policy.html?lang=${policyLang}`" target="_blank" rel="noopener noreferrer" :aria-label="$t('Navbar.PrivacyCookiePolicy')">
              <Icon name="cookie" :size="20" aria-hidden />
            </a>
          </template>
          <template #title>
            <Icon :name='navbarPageIcon' aria-hidden />
            <span class="truncate mt-1">{{ navbarPageTitle }}</span>
          </template>
          <template #notifications>
            <Notifications :notifications='notifications' />
          </template>
        </Navbar>
      </template>
      <ChatBot v-if="showChatBot" class="absolute"/>
      <router-view v-if='loaded && !showWelcomeModal'></router-view>
    </PageWrapper>
  </div>
  <!-- Send welcome email confirm modal -->
  <Modal v-model:open="impersonateModalConfirm" :title="$t('Account.Impersonate')" :trigger="impersonateModalRef" footer-alignment="center" small>
    <div class="w-full h-32 bg-primary-100 text-primary-500 px-4 py-4 text-sm">
      <ul>
        <li>{{ $t('Account.EditImpersonateMessage', {param: selImpersonateAccount?.username}) }}</li>
      </ul>
    </div>

    <template #footer>
      <CGButton variant="outline" @click="impersonateModalConfirm = false">{{ $t('General.Cancel') }}</CGButton>
      <CGButton @click="$startImpersonation(selImpersonateAccount.account_uuid, true)">{{ $t('General.Confirm') }}</CGButton>
    </template>
  </Modal>
</template>

<script>
import { defineComponent, getCurrentInstance, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import accountService from '@/services/account.service.js'
import companyService from '@/services/company.service.js'
import {
  EventType,
  OptionDataType,
  FormDataType,
  Roles,
  RolesScale,
  RolesBaseRoute,
  ServiceType,
  CgaContentType,
  AdditionalContentService,
  CgaTypeBoardValues,
  CgaTypeBoardStateMap,
  SmtpKeys
} from '@/common/constants'
import userService from '@/services/user.service'
import localizationService from '@/services/localization.service'
import awarenessService from '@/services/awareness.service'
import cyberIcon from '@/assets/icons/cyber-advisor.svg';
import colors from '@/utils/colors'
import { hasCompanyOptionValue } from '@/utils/companyOptions'
import { SystemFallbackLanguage } from '@/common/constants';

const NOTIFICATION_PERIOD=process.env.NOTIFICATION_PERIOD || 30000; // 30 sec

export default defineComponent({
  setup() {
    const { locale } = useI18n();
    let gp = getCurrentInstance().appContext.config.globalProperties;

    // set default global property values
    gp.$profile = {};
    gp.$getRoleBaseRoute = () => {};
    gp.$appVersion = "";

    return { locale, gp }
  },
  data() {
    return {
      navbarItems: [],
      language: null,
      userCompany: null,
      navbarPageTitle: '',
      navbarPageIcon: '',
      alertTitle: "",
      alertVariant: 'success',
      notifications: [],
      activeItem: '',
      roleRoute: '',
      loaded: null,
      ServiceType,
      Roles,
      RolesBaseRoute,
      colors: {},
      primaryColor: '',
      secondaryColor: '',
      contrastPrimary: '',
      contrastSecondary: '',
      cyberIcon,
      showLoader: false,
      showWelcomeModal: false,
      selImpersonateAccount: null,
      impersonateModalConfirm: false,
      impersonateModalRef: null,
      CgaContentType,
      AdditionalContentService,
      CgaTypeBoardValues
    };
},
  beforeMount() {
    this.gp.$appVersion = "2.2.8";
    const isProd = "true"
    if (isProd === "true") {
      this.gp.$isProdEnv = true
      console.log = () => {}
      console.info = () => {}
      console.debug = () => {}
    }

    this.$eventBus.$on('logout', this.logOut);

    this.$eventBus.$on('notification-click', (id) => {
      const index = this.notifications.findIndex((n) => n.id === id);
      if(index > -1) {
        this.markNotificationAsRead(this.notifications[index], index);

        let link = this.notifications[index].link;
        if (link) this.$router.push(link)
      }
    });

    this.$eventBus.$on('notification-set-all-read', () => {
      this.notifications.forEach((n, index) => this.markNotificationAsRead(n,index))
    })

    this.$eventBus.$on('show-alert', (event) => {
      this.alertTitle = event.title;
      this.alertVariant = event.variant;
      this.$refs['cg-alert'].showAlert(event?.seconds);
    });

    this.$eventBus.$on('loading', (value) => this.showLoader = value)

    this.$eventBus.$on('route-changed', (path) => {
      // set role route so that the navbar menu is updated
      let rRoute = path.split('/').filter(i=>i)

      // Derivated role names contains a substring such as "_37" tha should be removed to retrieve the base role
      let role = localStorage.getItem('activeRole');
      
      if (role && role.includes('_')) {
        role = role.substring(0, role.indexOf('_'));
      }

      this.roleRoute = this.RolesBaseRoute[role];

      // set current path as active item (joining the elements removes last slash of the path, required for validation of the active menu item)
      this.activeItem = '/' + rRoute.join('/')
      if (this.$refs.navbarRef) this.$refs.navbarRef.menuOpen = false;
    });

    this.$eventBus.$on('current-company', this.setCurrentCompany);
    this.$eventBus.$on('load-user', this.loadUser);
    this.$eventBus.$on('edit-impersonate', (account, modalRef) => {
      this.selImpersonateAccount = account;
      this.impersonateModalConfirm = true;
      this.impersonateModalRef = modalRef;
    });
  },

  beforeUnmount() {
    this.$eventBus.$off('loading');
    this.$eventBus.$off('show-alert');
    this.$eventBus.$off('notification-set-all-read');
    this.$eventBus.$off('notification-click');
    this.$eventBus.$off('route-changed');
    this.$eventBus.$off('logout');
    this.$eventBus.$off('current-company');
    this.$eventBus.$off('load-user');
  },

  computed: {
    // La pagina statica esiste solo in italiano e inglese: la lingua applicativa viene
    // normalizzata qui e passata via query string, perché la pagina non ha accesso al profilo.
    policyLang() {
      return String(this.locale || '').toLowerCase().indexOf('it') === 0 ? 'it' : 'en';
    },
    releaseMgmtPerm() {
      return this.$checkPermission([
        "phishing-view-all-campaign",
        "phishing-view-company-campaign",
        "phishing-view-all-remediation",
        "phishing-view-company-remediation"
      ])
    },
    userInitials() {
      const profile = this.$user.isDemoAccount && this.$user.impersonator || this.$profile;
      if (!profile) return '';

      return (profile.firstname && profile.lastname)?
        (profile.firstname.substr(0,1) + profile.lastname.substr(0,1)).toUpperCase() :
        (profile.username.substr(0,2).toUpperCase());
    },
    userName() {
      if (!this.$profile) return '';
      return (this.$profile.firstname && this.$profile.lastname)? (this.$profile.firstname + ' ' + this.$profile.lastname) : this.$profile.username;
    },
    impersonator(){
      if (!this.$user || !this.$user.impersonator) return this.$t('Account.Impersonator');
      return (this.$user.impersonator.firstname && this.$user.impersonator.lastname)? `${this.$user.impersonator.firstname}  ${this.$user.impersonator.lastname}` : this.$t('Account.Impersonator');
    },
    contentMgmtPerm () {
      return this.$checkPermission('frontend-view-content-mgmt') && 
        this.$checkPermission([
          'awareness-view-all-modules',
          'awareness-view-company-modules',
          'awareness-view-all-levels',
          'awareness-view-company-levels',
          'awareness-view-all-reserved',
          'awareness-view-company-reserved',
          'badge-view-all-badge-classes',
          'badge-view-company-badge-classes',
          'phishing-view-all-template',
          'phishing-view-template',
          'phishing-view-all-landingpage',
          'phishing-view-landingpage',
          'phishing-view-all-attachment',
          'phishing-view-attachment'
        ])
    },
    canViewReportOverview () {
      return this.$checkPermission([
        'reporting-view-all-service-participation',
        'reporting-view-company-service-participation',
        'reporting-view-account-service-participation',
        'reporting-view-all-account-team-rank',
        'reporting-view-company-account-team-rank',
        'reporting-view-team-account-team-rank',
        'reporting-view-account-account-team-rank',
        'reporting-view-all-account-global-rank',
        'reporting-view-company-account-global-rank',
        'reporting-view-account-account-global-rank',
        'reporting-view-all-team-rank',
        'reporting-view-company-team-rank',
        'reporting-view-team-team-rank',
        'reporting-view-account-team-rank',
      ])
    },
    canViewReportAwareness () {
      return this.$checkPermission([
        'reporting-view-all-module-times',
        'reporting-view-company-module-times',
        'reporting-view-team-module-times',
        'reporting-view-all-module-scores',
        'reporting-view-company-module-scores',
        'reporting-view-team-module-scores',
        'reporting-view-all-users-status',
        'reporting-view-company-users-status',
        'reporting-view-team-users-status',
        'reporting-view-all-stats',
        'reporting-view-company-stats',
        'reporting-view-team-stats',
      ])
    },
    canViewReportChannel () {
      return this.$checkPermission([
        'reporting-view-all-module-times',
        'reporting-view-company-module-times',
        'reporting-view-team-module-times',
        'reporting-view-all-users-status',
        'reporting-view-company-users-status',
        'reporting-view-team-users-status',
        'reporting-view-all-stats',
        'reporting-view-company-stats',
        'reporting-view-team-stats',
      ])
    },
    canViewReportPhishing () {
      return this.$checkPermission([
        'phishing-view-campaign-summary,phishing-view-all-company',
        'phishing-view-campaign-summary,phishing-view-company',
        'phishing-view-campaign-summary,phishing-view-all-campaign',
        'phishing-view-campaign-summary,phishing-view-company-campaign',
      ])
    },
    reportPerm() {
      return this.canViewReportOverview ||
        this.canViewReportAwareness ||
        this.canViewReportChannel ||
        this.canViewReportPhishing
    },
    hasAwarenessOrChannel () {
      return this.$profile.hasService(ServiceType.Awareness) || this.$profile.hasService(ServiceType.Channel);
    },
    visibleNavbarItems() {
      return this.navbarItems.filter(item => item.isVisible() && !item.isFooter);
    },
    footerNavbarItems() {
      return this.navbarItems.filter(item => item.isVisible() && item.isFooter);
    },
    showChatBot() {
      return !this.$route.path.includes('/cyber-advisor') && this.loaded === true && this.$profile.hasCyberAdvisorLicense;
    },
    isImpersonateCompanyDemoAccount() {
      return this.$currentCompany.company_options?.demo_company?.value && 
        ((this.$profile.currentRole === Roles.Student && this.$currentCompany.company_demo?.student_id) || (this.$profile.currentRole === Roles.Supervisor && this.$currentCompany.company_demo?.supervisor_id));
    }, 
    hasDefaultSmtp() {
      const customSmtp = this.$profile.hasRoles(Roles.Supervisor, Roles.CompanyAdmin, Roles.MSP) && 
        SmtpKeys.some(key => !!this.$company.company_options[key]?.value);
      
      const childPlainSettings = this.$profile.hasRoles(Roles.MSP) && 
        this.$profile.companies.filter(c => { 
          if (this.$company.company_id == c.company_id) return false;

          const hasSmtpOption = hasCompanyOptionValue(c.company_options, SmtpKeys);

          return c.created_by_company?.uuid && !hasSmtpOption;
          }
        );
      return childPlainSettings?.length > 0 || !customSmtp;
    },
  },
  mounted() {
    this.initNavbarItems();
    this.loadUser();
  },
  methods: {
    async loadUser(callbackFn, isLoader = true) {
      this.$eventBus.$emit('loading', isLoader);

      try {

        //prototype dei timezone
        this.gp.$timezones = Intl.supportedValuesOf('timeZone')
            .reduce((prev, el) => ({
              ...prev, [el.replace(' ', '')]: {
                id:   el,
                name: el,
              },
            }), {
              data:     Intl.supportedValuesOf('timeZone'),
              labels:   () => this.$timezones.data.map(el => ({ label: el, value: el })),
            })

        const userRes = await userService.getUser()
        let responses = await Promise.all([
          companyService.getServicesList(),
          localizationService.getLanguages(),
        ])
        const [servicesRes, languagesRes] = responses

        this.gp.$user = userRes.data
        this.gp.$services = servicesRes.data
          .reduce((prev, el) => ({
            ...prev, [el.name.replace(' ', '')]: {
              id:   el.service_id,
              name: el.name,
              display_name: el.display_name,
            },
          }), {
            data:     servicesRes.data,
            all:      () => this.$services.data.map(el => ({ id: el.service_id, name: el.display_name })),
            service:  (id) => this.$services.data.find(el => el.service_id === +id).name,
            labels:   (ids = []) => this.$services.data.filter(el => ids.includes(el.service_id))
              .map(el => ({ label: el.display_name, value: el.service_id })),
            fromName: (serviceName) => this.$services.data
              .find(el => el.name.toLowerCase() === serviceName.toLowerCase()).service_id,
            color:    (id) => colors.services[id],
            serviceLicenseMapping: () => this.$services.data.reduce((acc, el) => {
              acc[el.service_id] = el.display_name;
              return acc;
            }, {}),
          })

        responses = await Promise.all([
          accountService.getSingleAccount(this.$user.id, this.$user.company_id),
          companyService.getCompany(this.$user.company_id, true),
        ])
        const [profileRes, companyRes] = responses

        this.gp.$profile = reactive({ ...profileRes.data, origLicenseByService: profileRes.data.licenseByService })
        this.gp.$company = {
          ...companyRes.data,
          roles: this.$user.companies.find(c=>c.company_id==this.$user.company_id).roles
            .sort((a, b) => RolesScale.indexOf(this.$methods.getBaseRole(a.name)) - RolesScale.indexOf(this.$methods.getBaseRole(b.name))) }

        if (!this.$profile.companies || !this.$profile.companies.length) throw new Error('User does not have any company')

        // define for the profile whether the sidebar is visible based on the company option for the user's company or for the parent company
        const hideSidebar = this.$company.company_options.find(opt => opt.option_code === 'hide_side_bar')?.value;
        if (hideSidebar == null && this.$company.created_by_company?.customization_enabled && this.$company.created_by_company?.hide_side_bar != null) {
          this.$profile.hideSidebar = !!+this.$company.created_by_company.hide_side_bar;
        } else {
          this.$profile.hideSidebar = !!+hideSidebar;
        }

        // define for the profile whether the progress page is visible based on the company option for the user's company or for the parent company
        const hideProgressPage = this.$company.company_options.find(opt => opt.option_code === 'hide_progress_page')?.value;
        if (hideProgressPage == null && this.$company.created_by_company?.customization_enabled && this.$company.created_by_company?.hide_progress_page != null) {
          this.$profile.hideProgressPage = !!+this.$company.created_by_company.hide_progress_page;
        } else {
          this.$profile.hideProgressPage = !!+hideProgressPage;
        }

        this.locale = localStorage.getItem('language') || this.$profile.language || this.$company.default_language || SystemFallbackLanguage;
        
        let h = document.querySelector('html');
        h.setAttribute('lang', this.locale);

        // set title and favicon
        document.title = this.$methods.companyDisplayName(this.$company);

        const link = document.createElement('link');
        link.rel = 'icon';
        link.href = this.$company.created_by_company.customization_enabled ? this.$company.created_by_company.togglelogo : (this.$company.customization_enabled ? this.$company.togglelogo : '/favicon.png');
        document.head.appendChild(link);
        
        // set languages once the locale has been set
        let langArr = [], langObj = {}
        languagesRes.data.forEach(lang => {
          langArr.push({ value: lang.lang_code, label: lang.name})
          langObj[lang.lang_code] = lang.name
        })

        this.gp.$languages = langArr;
        this.gp.$languagesObj = langObj;

        this.setCompanyOptions(this.$company);

        this.gp.$currentCompany = reactive({...this.$company});

        this.gp.$getRoleBaseRoute = () => {
          const role = this.$methods.getBaseRole(this.$profile.currentRole);

          return (this.$profile && this.RolesBaseRoute[role]) || '';
        };
      
        /**
         * Permissions param is a string or an array of strings, where at lease one element of the array should be valid
         * (OR logical operator), instead the permissions inside a single element (split by comma) of the array should be all valid (AND logical operator)
         * @param permissions array|string
         * @return boolean
         */
        this.gp.$checkPermission = (permissions) => {
          if (!permissions || !this.$profile) return false

          return (typeof permissions == 'string' ? [permissions] : permissions)
            .some(p => p.split(',').every((i) => this.$profile.currentPermissions.includes(i.trim())))
        }

        this.gp.$getFlatTags = (tags, resArr) => {
          const ORGS_KEYS = ['label', 'team', 'value', 'show', 'filter', 'order', 'key']

          if (!resArr) resArr = []
          for (let key in tags) {
            if (ORGS_KEYS.includes(key)) continue

            // add tag and keep looking for the other tags in the tree
            resArr.push({ ...tags[key], 'key': key })
            resArr = this.$getFlatTags(tags[key], resArr)
          }
          return resArr
        }

        this.gp.$impersonating = localStorage.getItem('mask') ? localStorage.getItem('mask') : null

        this.gp.$stopImpersonation = (role) => {
          localStorage.clear()
          localStorage.setItem('initialRole', role && JSON.stringify(role) || null);
          this.$router.push('/').then(() => { location.reload() })
        }

        this.gp.$startImpersonation = (account_uuid, isEditImpersonator) => {
          if (account_uuid) {
            localStorage.setItem('mask', account_uuid)
            localStorage.setItem('isEditImpersonator', isEditImpersonator);
            localStorage.removeItem('activeRole')
            location.reload()
          }
        }

        this.$profile.hasRoles   = (...args) => [...args].some(id => this.$methods.isRole(this.$profile.currentRole, id))
        this.$profile.hasService = (...args) => [...args].every(id => this.$profile?.licenseByService.includes(id))

        this.$user.isDemoAccount = !!localStorage.getItem('isDemoAccount');
        this.$user.isEditImpersonator = localStorage.getItem('isEditImpersonator') === 'true';

        await this.setCgaType()
        await this.setCurrentRole(JSON.parse(localStorage.getItem('initialRole')), isLoader);

        // load all notifications and set interval for loading them periodically
        this.getNotifications();
        setInterval(this.getLastUnreadNotification, NOTIFICATION_PERIOD)

        this.calcColors();


        //SHOW WELCOME MODAL FOR TERMS & CONDITIONS
        if (this.$profile && !this.$profile.policy_agreed) {
          this.showWelcomeModal = true;
        }
      } catch (e) {
        this.logOut(e)
      }
      
      this.$eventBus.$emit('loading', false)
      if (callbackFn) callbackFn();
    },
    setDemoAccount() {
      // validate if the current company is selected as demo and 
      // if the current role is student or supervisor impersonate the company demo source account
      if (this.isImpersonateCompanyDemoAccount){
        localStorage.setItem('isDemoAccount', true);
        localStorage.setItem('language', this.$profile.language)
        localStorage.setItem('company', JSON.stringify({
          logo: this.$company.logo, 
          name: this.$company.name,
          displayname: this.$company.displayname,
          primaryColor: this.$company.company_options.primary_color.value,
          secondaryColor: this.$company.company_options.secondary_color.value,
          roles: this.$company.roles
        }));
        
        return this.$startImpersonation(this.$profile.currentRole == Roles.Student? this.$currentCompany.company_demo?.student_id : this.$currentCompany.company_demo?.supervisor_id);
      } else {
        this.loaded = true
      }
      
      // override the company logo and displayname with those saved in the storage
      if (this.$user.isDemoAccount) {

        // override the company logo, name and displayname with those saved in the storage
        const company = localStorage.getItem('company') && JSON.parse(localStorage.getItem('company'));
        if (company) {
          this.$company.logo = company.logo;
          this.$company.name = company.name;
          this.$company.displayname = company.displayname;
          this.$company.roles = company.roles;

          this.$currentCompany.logo = company.logo;
          this.$currentCompany.name = company.name;
          this.$currentCompany.displayname = company.displayname;
        }
        
        if (this.$user.impersonator) {
          this.$profile.firstname = this.$user.impersonator.firstname;
          this.$profile.lastname = this.$user.impersonator.lastname;
        }

        // override the profile language with the one saved in the storage
        this.$profile.language = localStorage.getItem('language') || this.$profile.language;
      }
    },
    calcColors() {
      // update primary and secondary colors if the colors are stored in the localStorage
      if (localStorage.getItem('company')){
        const company = JSON.parse(localStorage.getItem('company'));
        this.$company.company_options.primary_color.value = company.primaryColor;
        this.$company.company_options.secondary_color.value = company.secondaryColor;
      }
      this.primaryColor = this.getRGBColor(this.$company.company_options.primary_color.value, "primary");
      this.secondaryColor = this.getRGBColor(this.$company.company_options.secondary_color.value, "secondary");
      this.a11yColorPrimary = this.getRGBColor(this.getAccessibleColor(this.$company.company_options.primary_color.value), "a11yPrimary");
      this.a11yColorSecondary = this.getRGBColor(this.getAccessibleColor(this.$company.company_options.secondary_color.value), "a11ySecondary");

      this.colors = this.primaryColor + "; " + this.secondaryColor + "; " + this.a11yColorPrimary + "; " + this.a11yColorSecondary;
    },
    setCompanyOptions (company) {
      if(company.company_options.length) {
        const formDataType = Object.fromEntries(Object.entries(FormDataType).map(([key, value]) => [value, parseInt(key)]));

        company.company_options = company.company_options.reduce((prev, el) => {
          let value = null
  
          switch (OptionDataType[el.option_datatype]) {
            case 'integer' :
            case 'float' :
              value = +el.value
              break
            case 'boolean' :
              value = !!(+el.value)
              break
            case 'string' :
              // when the form data type is select or radio the value saved is numeric and rapresents the option selected
              if ([formDataType.select, formDataType.radio].includes(el.option_formtype)) 
                value = el.value? parseInt(el.value) : el.value;
              else 
                value = el.value
              break
          }
  
          return {
            ...prev, [el.option_code]: {
              dataType: OptionDataType[el.option_datatype],
              value:    value,
            },
          }
        }, {})
      }
    },
    async getLastUnreadNotification() {
      try {
        const latestNotification = await accountService.getLastUnreadNotification(this.$profile.account_uuid, this.$profile.company_id)
        const hasLatestNotification = this.notifications.some(nEvent => nEvent.creation_date == latestNotification.data)
        if (latestNotification.data && !hasLatestNotification) this.getNotifications(true)
      } catch (error) {
        console.error('getLastUnreadNotification ~ error:', error);
      }
    },
    async getNotifications(isLatest = false) {
      await accountService.getNotifications(this.$profile.account_uuid, this.$profile.company_id, this.$profile.language)
        .then((n) => {
          this.notifications = n && n.data
          if (isLatest){
            const mostRecentNotification = this.notifications.filter(n => n.event_type ==  EventType.InternalEvent)[0]
            if (mostRecentNotification) this.$eventBus.$emit('show-alert', { title: mostRecentNotification.description, variant: mostRecentNotification.is_error ? 'danger' : 'success' });
          }
          this.notifications.map((n) => {
            switch (n.event_type) {
              case EventType.ContentCGA:
                n.title = n.title || this.$t('Notification.ContentReleaseTitle', { service: 'Awareness' })
                n.description = n.description || this.$t('Notification.ContentReleaseDesc')
                n.link = '/student/modules'
                break
              case EventType.ContentCGC:
                n.title = n.title || this.$t('Notification.ContentReleaseTitle', { service: 'Channel' })
                n.description = n.description || this.$t('Notification.ContentReleaseDesc')
                n.link = '/student/modules'
                break
              default:
                break
            }
          })
        })
        .catch(() => true)
    },
    markNotificationAsRead(notification, index) {
      accountService.updateNotification(this.$profile.account_uuid, this.$profile.company_id, notification.id, { marked: 1 })
        .then(() => {
          notification.marked = 1;
          this.notifications[index] = notification;
        }, (err) => {
          console.log("Notification - error", err);
          this.$eventBus.$emit('show-alert', {
            title: this.$t('Notification.NotificationUpdateError'),
            variant: 'danger'
          })
        });
    },
    logOut(e) {
      if (e?.name === 'NavigationDuplicated')
        return location.reload() && console.error(e) && this.$router.go()

      this.$user = null
      localStorage.clear()
      console.error(e)
      window.location.href = '/logout'
    },
    isActiveItem(item) {
      if (!item) return false;
      const path = item.getPath();
      const isActive = path == this.roleRoute? this.activeItem == this.roleRoute : this.activeItem.startsWith(path);
      if (isActive) {
        this.navbarPageTitle = item.getLabel();
        this.navbarPageIcon = item.icon;
      }
      return isActive;
    },
    async setCurrentRole(role, isLoader = true) {
      // get role from the localStorage or the one with higher priority from the user's company
      if (!role) role = this.$company.roles.find(r => r.name === localStorage.getItem('activeRole')) || this.$company.roles[0];

      const oldRole = this.$profile.currentRole;
      const newRole = role.name;
      if (oldRole == newRole) return;

      this.$eventBus.$emit('loading', isLoader);

      this.$profile.currentRole = newRole;
      this.$profile.currentPermissions = role.permissions || [];
      localStorage.setItem('activeRole', newRole);

      // retrieve companies for the selected role
      let compArr = (await companyService.getCompanies({withCompanyOptions: true})).data?.data || [];

      // update all user companies with company tags and user's tags for each company
      let ucObj = null;
      compArr.forEach(c => {
        const uc = this.$user.companies?.find(uc => uc.company_id == c.company_id)
        c.userTags = uc?.user_tags || {};
        if (c.company_id == this.$company.company_id) ucObj = c;
      });
      if (!ucObj && this.$user.impersonator && !this.$user.isDemoAccount) {
        // in case of impersonate, the compArr contain the impersonator companies instead of those of the impersonated account, so we need to set them from the impersonated account company
        this.$company.userTags = this.$user.companies[0].user_tags || {};
      }
      this.$profile.companies = compArr.sort((a, b) => a.name.localeCompare(b.name));

      // update current company and profile service permissions
      await this.setCurrentCompany((ucObj || this.$company), null, isLoader);

      if (this.isImpersonateCompanyDemoAccount || this.$user.isDemoAccount) this.setDemoAccount();
      else this.loaded = true;
      
      const roleBaseRoute = this.$getRoleBaseRoute();
      if (oldRole && this.isImpersonateCompanyDemoAccount) {
        this.$router.push('/').then(() => { location.reload() })
      } else if (oldRole || (this.roleRoute !== roleBaseRoute && !oldRole)) {
        this.$router.replace(roleBaseRoute || '/').catch(() => {})
      }

      this.$eventBus.$emit('loading', false);
    },
    async setCurrentCompany(company, callback, isLoader = true) {
      // retrieve the company from the profile because is updated with the company and user's tags
      company = this.$profile.companies.find(c=>c.company_id == company.company_id) || company;
      this.setCompanyOptions(company);

      // update profile filtered tags for the current company
      let apiOrgFilter = {}
      if (!this.$profile.currentPermissions?.includes('admin-nofilter-orgs') && company.tags?.orgs && company.userTags) {
        const isTeamRestrict = this.$profile.currentPermissions?.includes('admin-restrict-team');
        
        for (const org in company.tags.orgs) {
          const orgData = company.tags.orgs[org];
          if (orgData.filter || (isTeamRestrict && orgData.team)) {
            apiOrgFilter[org] = company.userTags[org] || null;
          }
        }
      }
      this.$profile.filteredTags = Object.keys(apiOrgFilter).length ? apiOrgFilter : null

      // set service licenses to the user
      // Object.assign merge the two objects. Need to reset the company_demo object if in the new company is not present otherwise the object is not updated
      Object.assign(this.$currentCompany, {...company, ...!company.company_demo && {company_demo: null}});
      await this.updateProfileServicePermissions(company.company_id, isLoader);
      if (callback) callback();
    },
    setCgaType () {
      return awarenessService.getCgaType()
        .then(cgaTypeRes =>
          this.gp.$cgaType = cgaTypeRes.data.reduce((prev, el) => ({
            ...prev, [el.name.replace(' ', '')]: {
              id:          el.cga_type_id,
              name:        el.name,
              service:     el.service_id,
              description: el.description,
              additional: el.additional,
              single_attempt: el.single_attempt,
              board: el.board
            },
          }), {
            data:         cgaTypeRes.data,
            userCgaTypes: cgaTypeRes.data.filter(el => (this.$profile.hasService(el.service_id) || el.additional === CgaContentType.ADDITIONAL))
                            .map(el => el.cga_type_id),
            all:          () => this.gp.$cgaType.data.filter(el => el.service_id !== null)
              .map(el => el.cga_type_id),
            service:      (id) => this.gp.$cgaType.data
                .filter(el => id === ServiceType.Remediation ? el.service_id != null : el.service_id === id)
                ?.map(el => this.gp.$cgaType.type(el.cga_type_id)),
            type:         (id) => this.gp.$cgaType.data.filter(el => el.cga_type_id === id)
              ?.map(el => ({
                id:          el.cga_type_id,
                name:        el.name,
                service:     el.service_id,
                description: el.description,
                additional:  el.additional,
                single_attempt: el.single_attempt,
                board: el.board
              }))[0],
            labels:       (ids = []) => this.gp.$cgaType.data.filter(el => (el.service_id !== null || el.additional === CgaContentType.ADDITIONAL))
              .filter(el => ids.includes(el.cga_type_id))
              .map(el => ({ label: el.name, value: el.cga_type_id })),
            withAdditionalContent:  () => this.gp.$cgaType.data.filter(el => (el.service_id !== null || el.additional === CgaContentType.ADDITIONAL))
                .map(el => el.cga_type_id),
            additionalContentLabels:  (ids = []) => this.gp.$cgaType.data.filter(el => (el.service_id === null && el.additional === CgaContentType.ADDITIONAL) )
                .filter(el => ids.includes(el.cga_type_id))
                .map(el => ({ label: el.name, value: el.cga_type_id })),
            boardTrainingTypes: () => {
              const seen = new Set();
              return this.gp.$cgaType.data
                .filter(el => el.board !== null && !seen.has(el.board) && seen.add(el.board))
                .map(el => {
                  const label = CgaTypeBoardValues[el.board];
                  return {
                    label,
                    value: el.board+'',
                    state: CgaTypeBoardStateMap[label] || 'unknown'
                  };
                });
            },
            boardTrainingTypesMap: () => {
              return this.gp.$cgaType.boardTrainingTypes().reduce((map, type) => {
                map[type.value] = type;
                return map;
              }, {});
            }
          }),
        )
        .catch(e => this.logOut(e))
    },
    async updateProfileServicePermissions(companyId, isLoader = true) {
      // retrieve current activeRole
      this.$eventBus.$emit('loading', isLoader);
      const activeRole = localStorage.getItem('activeRole');

      if (!this.$methods.isRole(activeRole, Roles.Student)) {
        // retrieve licenses for the currentCompany and evaluate service permissions based on the peoples that are assigned to the licenses
        const licensesRes = await companyService.getLicenses(companyId || this.$currentCompany.company_id);
        this.$profile.licenseByService = licensesRes.data.filter(l => l.active_licenses > 0 || l.has_grace_licenses).map(l => l.service_id);

        // Check if the cyberAdvisor license for the current company exists and is still valid
        const cyberAdvisorLicense = licensesRes.data.find(l => l.service_id === ServiceType.CyberAdvisor);
        const expiryDateWithGrace = this.$dateTime.fromSQL(cyberAdvisorLicense?.expiry_date).plus({ days: cyberAdvisorLicense?.grace_period || 0 });
        this.$profile.hasCyberAdvisorLicense = expiryDateWithGrace > this.$dateTime.now();
      } else {
        this.$profile.hasCyberAdvisorLicense = this.$profile.origLicenseByService.includes(ServiceType.CyberAdvisor);
        this.$profile.licenseByService = this.$profile.origLicenseByService;
      }
      this.$eventBus.$emit('loading', false)
    },
    getRGBColor(hex, type) {
      let color = hex.replace(/#/g, "");
      // rgb values
      const r = parseInt(color.substr(0, 2), 16);
      const g = parseInt(color.substr(2, 2), 16);
      const b = parseInt(color.substr(4, 2), 16);
      return `--color-${type}: ${r}, ${g}, ${b};`;
    },
    getAccessibleColor(hex) {
      let color = hex.replace(/#/g, "");
      // rgb values
      const r = parseInt(color.substr(0, 2), 16);
      const g = parseInt(color.substr(2, 2), 16);
      const b = parseInt(color.substr(4, 2), 16);
      const yiq = (r * 299 + g * 587 + b * 114) / 1000;
      return yiq >= 128 ? "#000000" : "#FFFFFF";
    },
    initNavbarItems() {
      this.navbarItems = [{
        getLabel: ()=>{return this.$t('Navbar.Dashboard')},
        getPath: ()=>{return this.roleRoute},
        icon: 'home',
        isVisible: ()=>{return true}
      }, {
        getLabel: ()=>{return this.$t('Navbar.Progress')},
        getPath: ()=>{return this.roleRoute + '/progress'},
        icon: 'analytics',
        isVisible: ()=>{return this.$profile.hasRoles(Roles.Student) && !this.$profile.hideProgressPage}
      }, {
        getLabel: ()=>{return this.$t('Navbar.Plan')},
        getPath: ()=>{return this.roleRoute + '/modules'},
        icon: 'courses',
        isVisible: ()=>{return this.$profile.hasRoles(Roles.Student) && (this.hasAwarenessOrChannel || AdditionalContentService.show)}
      }, {
        getLabel: ()=>{return this.$t('Navbar.CompanyManagement')},
        getPath: ()=>{return this.roleRoute + '/company'},
        icon: 'list',
        isVisible: ()=>{return this.$profile.hasRoles(Roles.CSM, Roles.Admin, Roles.MSP) && this.$checkPermission(['frontend-view-company, admin-view-all-company', 'frontend-view-company, admin-view-company'])}
      }, {
        getLabel: ()=>{return this.$t('Navbar.CompanyManagement')},
        getPath: ()=>{return this.roleRoute + '/company/' + this.$user.company_id},
        icon: 'list',
        isVisible: ()=>{return this.$profile.hasRoles(Roles.CompanyAdmin) && this.$checkPermission(['frontend-view-company, admin-view-company'])}
      }, {
        getLabel: ()=>{return this.$t('Navbar.AccountManagement')},
        getPath: ()=>{return this.roleRoute + '/account'},
        icon: 'user',
        isVisible: ()=>{return this.$profile.hasRoles(Roles.CSM, Roles.Admin, Roles.MSP, Roles.ContentManager) && this.$checkPermission(['admin-view-all-account, frontend-view-account-mgmt', 'admin-view-company-account, frontend-view-account-mgmt'])}
      }, {
        getLabel: ()=>{return this.$t('Navbar.UsersStats')},
        getPath: ()=>{return this.roleRoute + '/stats/' + this.$profile.company_id},
        icon: 'userStats',
        isVisible: ()=>{return this.$profile.hasRoles(Roles.Supervisor,Roles.CompanyAdmin) && this.$checkPermission(['frontend-view-user-stats, reporting-view-company-users-stats']) && this.hasAwarenessOrChannel}
      } , {
        getLabel: ()=>{return this.$t('Navbar.UsersStats')},
        getPath: ()=>{return this.roleRoute+'/stats/' + this.$profile.company_id+'/' + this.$profile.team},
        icon: 'userStats',
        isVisible: ()=>{return this.$profile.hasRoles(Roles.Teamleader) && this.$checkPermission(['frontend-view-user-stats, reporting-view-team-users-stats']) && this.hasAwarenessOrChannel}
      }, {
        getLabel: ()=>{return this.$t('Navbar.EmailStats')},
        getPath: ()=>{return this.roleRoute + '/email-stats'},
        icon: 'mail',
        isVisible: ()=>{return this.$profile.hasRoles(Roles.CSM, Roles.Admin, Roles.MSP, Roles.SupervisorRoles, Roles.CompanyAdmin) && this.hasDefaultSmtp && this.$checkPermission(['admin-view-all-email-stats', 'admin-view-company-email-stats'])}
      } , {
        getLabel: ()=>{return this.$t('Navbar.ReportOverview')},
        getPath: ()=>{return this.roleRoute + '/report'},
        icon: 'chart',
        isVisible: ()=>{return this.$profile.hasRoles(Roles.Teamleader,Roles.Supervisor,Roles.CompanyAdmin, Roles.CSM, Roles.Admin, Roles.MSP) && this.reportPerm}
      }, {
        getLabel: ()=>{return this.$t('Navbar.DownloadArea')},
        getPath: ()=>{return this.roleRoute + '/download-area'},
        icon: 'download',
        isVisible: ()=>{return this.$checkPermission(['awareness-view-all-company-files','awareness-view-company-files', 'frontend-view-company-files']) && this.hasAwarenessOrChannel}
      }, {
        getLabel: ()=>{return this.$t('Navbar.ContentManagement')},
        getPath: ()=>{return this.roleRoute + '/content-management'},
        icon: 'courses',
        isVisible: ()=>{return this.$profile.hasRoles(Roles.CSM, Roles.Admin, Roles.ContentManager) && this.contentMgmtPerm}
      }, {
        getLabel: ()=>{return this.$t('Navbar.ContentManagement')},
        getPath: ()=>{return this.roleRoute + '/content-management/badge'},
        icon: 'courses',
        isVisible: ()=>{return this.$profile.hasRoles(Roles.MSP) && this.contentMgmtPerm}
      }, {
        getLabel: ()=>{return this.$t('Navbar.ContentManagement')},
        getPath: ()=>{return this.roleRoute + '/content-management/reserved'},
        icon: 'courses',
        isVisible: ()=>{return this.$profile.hasRoles(Roles.CompanyAdmin) && this.contentMgmtPerm}
      }, {
        getLabel: ()=>{return this.$t('Navbar.ReleaseManagement')},
        getPath: ()=>{return this.roleRoute + '/release-management'},
        icon: 'calendar',
        isVisible: ()=>{return (this.$profile.hasRoles(Roles.CSM, Roles.Admin, Roles.MSP) || (this.$profile.hasRoles(Roles.CompanyAdmin) && this.$profile.hasService(ServiceType.Phishing))) && this.releaseMgmtPerm}
      }, {
        // TODO Should 'awareness-view-all-certificate-item-menu' check be removed after certificates are ready?
        getLabel: ()=>{return this.$t('Navbar.Certificates')},
        getPath: ()=>{return this.roleRoute + '/certificates'},
        icon: 'badge',
        isVisible: ()=>{return this.$profile.hasRoles(Roles.Student,Roles.Supervisor,Roles.CompanyAdmin) && this.$checkPermission(['awareness-view-all-certificate-item-menu'])}
      }, {
        getLabel: ()=>{return this.$t('Navbar.Acknowledgments')},
        getPath: ()=>{return this.roleRoute + '/acknowledgment'},
        icon: 'shield',
        isVisible: ()=>{return this.$profile.hasRoles(Roles.Student)}
      }, {
        getLabel: ()=>{return this.$t('Navbar.ReservedArea')},
        getPath: ()=>{return this.roleRoute + '/reserved-area'},
        icon: 'file',
        isVisible: ()=>{return this.$profile.hasRoles(Roles.Student)}
      }, {
        getLabel: ()=>{return this.$t('Navbar.CyberAdvisor')},
        getPath: ()=>{return '/cyber-advisor'},
        icon: 'cyberIcon',
        isVisible: ()=>{return this.$profile.hasCyberAdvisorLicense}, 
        isFooter: true
      }, {
        getLabel: ()=>{return this.$t('Navbar.Profile')},
        getPath: ()=>{return '/profile'},
        icon: 'user',
        isVisible: ()=>{return !this.$impersonating},
        hasClass: 'block lg:hidden',
        isFooter: true
      }, {
        getLabel: ()=>{return this.$t('Navbar.Settings')},
        getPath: ()=>{return this.roleRoute + '/settings'},
        icon: 'settings',
        isVisible: ()=>{return this.$profile.hasRoles(Roles.Admin)},
        isFooter: true
      }, {
        getLabel: ()=>{return this.$t('Navbar.Help')},
        getPath: ()=>{return '/help'},
        icon: 'help',
        isVisible: ()=>{return true},
        isFooter: true
      }];
    }
  },
});
</script>
<style>
@tailwind base;
@tailwind components;
@tailwind utilities;

body {
  @apply font-sans antialiased;
  @apply bg-white text-primary-500;
  @apply overflow-x-hidden;
}

.user-sidebar-wrapper {
  @apply absolute top-8 right-8 xl:top-11 xl:right-11 z-[41];
}

.user-sidebar {
  @apply w-full;
  @apply flex flex-col h-full;
}

.user-sidebar-actions {
  @apply flex justify-end grid-cols-1 gap-3 auto-cols-auto grid-flow-col-dense;
  @apply mb-6;
}

.user-sidebar-actions > * {
  @apply w-auto;
}

*::-webkit-scrollbar {
  width: 0;
}

.table-wrapper::-webkit-scrollbar,
.table-wrapper *::-webkit-scrollbar,
.page-wrapper-content::-webkit-scrollbar,
.page-wrapper-content *::-webkit-scrollbar {
  width: 8px !important;
  visibility: visible !important;
}

* {
  scrollbar-width: thin !important;
  scrollbar-color: auto !important;
  -ms-overflow-style: auto !important;
}

.table-wrapper,
.table-wrapper * {
  scrollbar-color: #adb4c7 transparent !important;
}

.table-wrapper::-webkit-scrollbar-thumb {
  background: slategray;
  border-radius: 8px;
}

.cyber-button {
  @apply relative;
  @apply inline-flex items-center justify-center;
  @apply w-[42px] h-[42px] text-2xl text-custom-contrastPrimary lg:text-violet-700;
  @apply bg-transparent lg:bg-white;
  @apply border border-transparent lg:border-primary-100;
}

/*--------------------------------------------------------------
# Accessibility
--------------------------------------------------------------*/
/* Text meant only for screen readers. */
.screen-reader-text {
  clip: rect(1px, 1px, 1px, 1px);
  position: absolute !important;
  height: 1px;
  width: 1px;
  overflow: hidden;
  word-wrap: normal !important;
  /* Many screen reader and browser combinations announce broken words as they would appear visually. */
}
.screen-reader-text:focus {
  background-color: black;
  border-radius: 4px;
  box-shadow: 0 0 2px 2px rgba(0, 0, 0, 0.1);
  clip: auto !important;
  color: #fff;
  display: block;
  font-size: 14px;
  font-weight: bold;
  height: auto;
  left: 0.3em;
  padding: 0.8em 1.5em;
  width: auto;
  z-index: 100000;
}

/* Do not show the outline on the skip link target. */
#main[tabindex="-1"]:focus {
  outline: 0;
}

@layer base {
  @font-face {
    font-family: 'Epilogue';
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: url(../public/fonts/Epilogue-400.woff2) format('woff2'), url(../public/fonts/Epilogue.ttf);
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC,
      U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
  }
  @font-face {
    font-family: 'Epilogue';
    font-style: normal;
    font-weight: 500;
    font-display: swap;
    src: url(../public/fonts/Epilogue-500.woff2) format('woff2'), url(../public/fonts/Epilogue.ttf);
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC,
      U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
  }
  @font-face {
    font-family: 'Epilogue';
    font-style: normal;
    font-weight: 600;
    font-display: swap;
    src: url(../public/fonts/Epilogue-600.woff2) format('woff2'), url(../public/fonts/Epilogue.ttf);
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC,
      U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
  }
  @font-face {
    font-family: 'Epilogue';
    font-style: normal;
    font-weight: 700;
    font-display: swap;
    src: url(../public/fonts/Epilogue-700.woff2) format('woff2'), url(../public/fonts/Epilogue.ttf);
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC,
      U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
  }
}
</style>
